/**
 * Centralized timestamp formatters for the admin UI.
 *
 * Returns a formatter bundle bound to a specific IANA timezone — the caller
 * (typically via the `useFmt` hook) decides which zone to pin to. The hook
 * reads the operator-controlled choice from `displayStore`, so flipping the
 * toggle in the Layout re-renders every visible timestamp.
 *
 * Input is an ISO 8601 string (what D1 / our APIs return). null / empty
 * inputs render as an em-dash so callers don't need to ternary every cell.
 */

export interface Formatters {
  fmtDateTime: (iso: string | null | undefined) => string;
  fmtDate: (iso: string | null | undefined) => string;
  fmtTime: (iso: string | Date | null | undefined) => string;
}

/**
 * Parse a backend timestamp into a Date.
 *
 * Most APIs return ISO 8601 ("2026-05-21T03:03:50.987Z") — JS Date handles
 * that fine. But D1's `datetime('now')` emits the SQLite default shape
 * "YYYY-MM-DD HH:MM:SS" (UTC, no trailing Z). JS Date treats the no-suffix
 * form as LOCAL time, which makes timestamps drift by the browser's TZ
 * offset (7 hours if browser sits in PDT, etc).
 *
 * Detect the SQLite shape and force-attach Z so it parses as UTC.
 */
const SQLITE_NAIVE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

function parseBackendTimestamp(iso: string): Date {
  if (SQLITE_NAIVE.test(iso)) {
    return new Date(iso.replace(' ', 'T') + 'Z');
  }
  return new Date(iso);
}

export function createFormatters(tz: string): Formatters {
  const dateTimeFmt = new Intl.DateTimeFormat('zh-CN', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const dateFmt = new Intl.DateTimeFormat('zh-CN', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const timeFmt = new Intl.DateTimeFormat('zh-CN', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return {
    fmtDateTime(iso) {
      if (iso === null || iso === undefined || iso === '') return '—';
      const d = parseBackendTimestamp(iso);
      if (Number.isNaN(d.getTime())) return '—';
      return dateTimeFmt.format(d);
    },
    fmtDate(iso) {
      if (iso === null || iso === undefined || iso === '') return '—';
      const d = parseBackendTimestamp(iso);
      if (Number.isNaN(d.getTime())) return '—';
      return dateFmt.format(d);
    },
    fmtTime(iso) {
      if (iso === null || iso === undefined || iso === '') return '—';
      const d = iso instanceof Date ? iso : parseBackendTimestamp(iso);
      if (Number.isNaN(d.getTime())) return '—';
      return timeFmt.format(d);
    },
  };
}
